import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiPercent, FiCalendar, FiCheckCircle, FiXCircle, FiLayers, FiAlertCircle } from 'react-icons/fi';
import CouponService from '../services/CouponService';

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form State
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    discountType: 'PERCENTAGE',
    discountValue: '',
    minimumAmount: '0',
    maxUses: '500',
    expiryDate: '',
    eventId: 'ALL',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const couponsRes = await CouponService.getAllCoupons();

      if (couponsRes.data?.success) {
        setCoupons(couponsRes.data.coupons || []);
      }
    } catch (err) {
      console.error('Error fetching coupons data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.code.trim() || !formData.discountValue) {
      setError('Please fill in Coupon Code and Discount Value.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await CouponService.addCoupon({
        ...formData,
        code: formData.code.trim().toUpperCase(),
        discountValue: Number(formData.discountValue),
        minimumAmount: Number(formData.minimumAmount || 0),
        maxUses: Number(formData.maxUses || 500),
        eventId: formData.eventId === 'ALL' ? null : formData.eventId,
      });

      if (res.data?.success) {
        setSuccess('Coupon created successfully!');
        setShowModal(false);
        setFormData({
          code: '',
          title: '',
          discountType: 'PERCENTAGE',
          discountValue: '',
          minimumAmount: '0',
          maxUses: '500',
          expiryDate: '',
          eventId: 'ALL',
        });
        fetchData();
      } else {
        setError(res.data?.message || 'Failed to create coupon.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Server error creating coupon.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await CouponService.toggleStatus(id);
      fetchData();
    } catch (err) {
      alert('Error updating coupon status');
    }
  };

  const handleDelete = async (id, code) => {
    if (window.confirm(`Are you sure you want to delete coupon '${code}'?`)) {
      try {
        await CouponService.deleteCoupon(id);
        fetchData();
      } catch (err) {
        alert('Error deleting coupon');
      }
    }
  };

  return (
    <div className="space-y-6 max-w-7xl text-left">
      {/* Top Action Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2 tracking-tight">
            <FiPercent className="text-purple-700" /> Coupon & Promo Management
          </h1>
          <p className="text-xs font-medium text-slate-500 mt-1">
            Create and manage event-specific or global discount promo codes
          </p>
        </div>
        <button
          onClick={() => {
            setError('');
            setSuccess('');
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-5 py-2.5 bg-purple-700 hover:bg-purple-800 text-white rounded-xl text-xs font-bold transition shadow-sm"
        >
          <FiPlus className="text-sm" /> Create New Coupon
        </button>
      </div>

      {/* Notifications */}
      {success && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs font-semibold flex items-center gap-2">
          <FiCheckCircle className="text-emerald-600" /> {success}
        </div>
      )}
      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs font-semibold flex items-center gap-2">
          <FiAlertCircle className="text-rose-600" /> {error}
        </div>
      )}

      {/* Coupons Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-slate-500 text-xs font-medium">Loading coupons data...</div>
        ) : coupons.length === 0 ? (
          <div className="p-12 text-center text-slate-500 space-y-2">
            <FiPercent className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-extrabold text-slate-700">No promo coupons created yet</p>
            <p className="text-xs text-slate-500 font-medium">Click "Create New Coupon" to set up your first promo code.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-slate-700 text-xs">
              <thead className="bg-slate-100/70 text-slate-600 uppercase font-extrabold border-b border-slate-200 text-[11px] tracking-wider">
                <tr>
                  <th className="p-4">Coupon Code</th>
                  <th className="p-4">Discount</th>
                  <th className="p-4">Applicable Event</th>
                  <th className="p-4">Min. Amount</th>
                  <th className="p-4">Uses</th>
                  <th className="p-4">Expiry</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {coupons.map((c) => (
                  <tr key={c._id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4">
                      <span className="font-mono font-bold text-purple-800 bg-purple-50 border border-purple-200 px-2.5 py-1 rounded-lg">
                        {c.code}
                      </span>
                    </td>
                    <td className="p-4 font-bold text-slate-900">
                      {c.discountType === 'PERCENTAGE' ? `${c.discountValue}% OFF` : `₹${c.discountValue} OFF`}
                    </td>
                    <td className="p-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                          c.eventId ? 'bg-indigo-50 text-indigo-800 border-indigo-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'
                        }`}
                      >
                        <FiLayers className="text-[10px]" />
                        {c.eventTitle}
                      </span>
                    </td>
                    <td className="p-4 text-slate-600 font-medium">
                      {c.minimumAmount > 0 ? `₹${c.minimumAmount}` : 'No Min.'}
                    </td>
                    <td className="p-4 text-slate-900 font-bold">
                      {c.usedCount} / {c.maxUses || '∞'}
                    </td>
                    <td className="p-4 text-slate-600 font-medium">
                      {c.expiryDate ? new Date(c.expiryDate).toLocaleDateString('en-IN') : 'No Expiry'}
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => handleToggleStatus(c._id)}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold border transition ${
                          c.status === 'ACTIVE'
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : 'bg-rose-50 text-rose-800 border-rose-200'
                        }`}
                      >
                        {c.status}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => handleDelete(c._id, c.code)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Delete Coupon"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal: Create New Coupon */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs">
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg overflow-hidden shadow-xl text-left">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <FiPercent className="text-purple-700" /> Create Promo Coupon
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg"
              >
                <FiXCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateCoupon} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">COUPON CODE *</label>
                <input
                  type="text"
                  placeholder="e.g. TAPCOE20 or SUMMER50"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 uppercase font-mono font-bold focus:border-purple-600 focus:outline-none"
                  required
                />
              </div>



              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">DISCOUNT TYPE</label>
                  <select
                    value={formData.discountType}
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-xs text-slate-900 font-medium focus:border-purple-600 focus:outline-none"
                  >
                    <option value="PERCENTAGE">Percentage (%) Off</option>
                    <option value="FLAT">Flat Amount (₹) Off</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">DISCOUNT VALUE *</label>
                  <input
                    type="number"
                    placeholder={formData.discountType === 'PERCENTAGE' ? 'e.g. 20 (for 20%)' : 'e.g. 100 (for ₹100)'}
                    value={formData.discountValue}
                    onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 font-medium focus:border-purple-600 focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">MIN. TICKET AMOUNT (₹)</label>
                  <input
                    type="number"
                    placeholder="0 for no minimum"
                    value={formData.minimumAmount}
                    onChange={(e) => setFormData({ ...formData, minimumAmount: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 font-medium focus:border-purple-600 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">USAGE LIMIT (MAX USES)</label>
                  <input
                    type="number"
                    placeholder="500"
                    value={formData.maxUses}
                    onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 font-medium focus:border-purple-600 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">EXPIRY DATE (OPTIONAL)</label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 font-medium focus:border-purple-600 focus:outline-none"
                />
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 bg-purple-700 hover:bg-purple-800 text-white rounded-xl text-xs font-bold transition disabled:opacity-50 shadow-sm"
                >
                  {submitting ? 'Creating...' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coupons;
